import type { ReactNode } from "react";
import { View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import { PrimaryTitleText } from "./PrimaryTitleText";
import { SupportingSubtitleText } from "./SupportingSubtitleText";
import { MetaText } from "./MetaText";

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  titleRight?: ReactNode;
  trailing?: ReactNode;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subtitleStyle?: StyleProp<TextStyle>;
  subtitleVariant?: "supporting" | "meta";
};

/**
 * Structural header for ordinary cards. It composes the canonical title and
 * supporting subtitle pair while leaving icons, chips, and trailing controls
 * to the surface that owns them.
 */
export function CardHeader({
  title,
  subtitle,
  leading,
  titleRight,
  trailing,
  style,
  titleStyle,
  subtitleStyle,
  subtitleVariant = "supporting",
}: Props) {
  return (
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        },
        style,
      ]}
    >
      {leading ? (
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {leading}
        </View>
      ) : null}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 7,
            flexWrap: "wrap",
          }}
        >
          <PrimaryTitleText style={titleStyle}>{title}</PrimaryTitleText>
          {titleRight}
        </View>
        {subtitle ? (
          subtitleVariant === "meta" ? (
            <MetaText style={subtitleStyle}>{subtitle}</MetaText>
          ) : (
            <SupportingSubtitleText style={subtitleStyle}>
              {subtitle}
            </SupportingSubtitleText>
          )
        ) : null}
      </View>
      {trailing}
    </View>
  );
}

export default CardHeader;
